import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Chip,
  Rating,
  Stack,
  Container,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Phone,
  Instagram,
  Facebook,
  YouTube,
  Flag,
  ArrowBackIos,
  ArrowForwardIos,
  LanguageTwoTone,
  MapOutlined,
  Check,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { request } from '../../Utils/request';
import { togglePlaceFavorite } from '../../services/API/Api';
import { updatePlace, updatePlaceReview } from '../../store/slices/PlacesSlice';
import { StaticMap } from '../../Components/Map';
import AddReviewDialog from '../../Components/AddReviewDialog';
import ReviewCard from '../../Components/ReviewCard';
import StarIcon from '@mui/icons-material/Star';
import ImageWithLoader from '../../Components/ImageWithLoader';
import CircleIcon from '@mui/icons-material/Circle';
import PlaceTabs from '../../Components/PlaceTabs';
import PlaceRelatedPost from './PlaceRelatedPost';
import ReportModal from '../../Components/ReportModal';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

interface Place {
  _id: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  type: string;
  featured: boolean;
  location: {
    type: string;
    zoom: number;
    lat: number;
    lng: number;
  };
  address: string;
  owner_details: {
    name: string;
    email: string;
    phone: string;
  };
  files: Array<{
    fileURL: string;
    fileType: string;
  }>;
  rating?: number;
  // reviews: {
  //   reviews: Review[];
  // };
  distance?: string;
  createdAt: string;
  updatedAt: string;
  social: { key: string; value: string }[];
  isLiked: boolean;
  topReviews?: Review[];
  isVisited: boolean;
  reports?: Array<{
    profileId: string;
    reason: string;
    description: string;
    status: string;
    reportedAt: string;
    _id: string;
  }>;
}

interface Review {
  user: {
    _id: string;
    username: string;
    avatar?: string; // optional future support
    profilePhoto: {
      fileKey: string;
      fileType: string;
      featured: boolean;
      url: string;
    };
  };
  rating: number;
  review: string;
  ratedAt: string;
}

interface ReviewResponse {
  totalReviews: number;
  averageRating: number;
  reviews: Review[];
  currentUserRating: number;
  currentUserReview: string;
}

type ReviewInput = Pick<Review, 'rating' | 'review'>;

const PlaceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const [place, setPlace] = useState<Place | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const userLocation = useSelector((state: RootState) => state.location.location);
  const user = useSelector((state: RootState) => state.user);

  const [openReview, setOpenReview] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const placeData = useSelector((state: RootState) => state.places.places);
  const [tabValue, setTabValue] = useState<number>(0);



  useEffect(() => {
    if (id) {
      fetchPlaceDetails(id);
    }
  }, [id, dispatch]);

  // Calculate distance when user location and place data are available
  useEffect(() => {
    const calculatePlaceDistance = async () => {
      if (userLocation?.lat && userLocation?.lng && place?.location?.lat && place?.location?.lng) {
        try {
          const calculatedDistance = await calculateDistance(
            userLocation.lat,
            userLocation.lng,
            place.location.lat,
            place.location.lng
          );
          setDistance(calculatedDistance);
        } catch (error) {
          console.error('Error calculating distance:', error);
          setDistance(null);
        }
      }
    };

    calculatePlaceDistance();
  }, [userLocation, place]);

  const fetchPlaceDetails = useCallback(async (placeId: string) => {
    try {
      const response = await request<{ success: boolean; data: Place, auth: boolean }>(`/places/${placeId}`, { authorization: true });
      if (response.success) {
        console.log('response.data place by id', response.data);
        setPlace(response.data);
        // Update the global store so other components have the full place data
        dispatch(updatePlace(response.data));
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      // setLoading(false);
    }
  }, [dispatch]);

  const createReview = async (placeId: string, reviewData: ReviewInput) => {
    if (!place) return;
    
    try {
      const res = await request<{ success: boolean; data: ReviewResponse }>(`/places/${placeId}/review`, {
        authorization: true, 
        method: 'POST', 
        body: reviewData
      });
      console.log('create review res', res);
      if (res.success === true) {
        // Update the store with the new review data
        dispatch(updatePlaceReview({
          _id: placeId,
          reviewData: res.data
        }));
        
        // Also update local state to reflect changes immediately
        const updatedPlace = {
          ...place,
          totalReviews: res.data.totalReviews,
          averageRating: res.data.averageRating,
          reviews: res.data.reviews,
          currentUserRating: res.data.currentUserRating,
          currentUserReview: res.data.currentUserReview,
          topReviews: res.data.reviews
        };
        setPlace(updatedPlace);
      }
    } catch (error) {
      console.error('Error creating review:', error);
    }
  };

  const handleFavoriteToggle = async (placeId: string) => {
    if (!place) return;
    
    try {
      const res = await togglePlaceFavorite(placeId);
      
      if (res.success) {
        // Toggle the isLiked state
        const newIsLiked = !place.isLiked;
        // Create the updated place object
        const updatedPlace = { ...place, isLiked: newIsLiked };
        // Update local state first
        setPlace(updatedPlace);
        // Then update the store
        dispatch(updatePlace(updatedPlace));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Check if current user has already reported this place
  const currentUserId = user?.user?._id || user?.user?.id;
  const isAlreadyReported = place?.reports?.some(
    (report) => report.profileId === currentUserId
  ) || false;

  const handleReportPlace = async (reportData: { reason: string; description: string }) => {
    if (!place || !currentUserId) return;
    
    try {
      const res = await request<{ success: boolean; data?: any }>(`/places/${place._id}/report`, {
        authorization: true,
        method: 'POST',
        body: reportData,
      });
      console.log('post report  res', res);
      
      if (res.success) {
        // Create new report object
        const newReport = {
          profileId: currentUserId,
          reason: reportData.reason,
          description: reportData.description,
          status: 'pending',
          reportedAt: new Date().toISOString(),
          _id: res.data?._id || `temp-${Date.now()}`, // Use API response ID if available, otherwise temp ID
        };

        // Update reports array
        const updatedReports = [...(place.reports || []), newReport];
        
        // Create updated place object
        const updatedPlace = { 
          ...place, 
          reports: updatedReports 
        };
        
        // Update local state first
        setPlace(updatedPlace);
        
        // Then update Redux store
        dispatch(updatePlace(updatedPlace));
        
        alert('Thank you for your report. We will review it shortly.');
      }
    } catch (err) {
      console.error('err', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: place?.name,
        text: place?.description,
        url: window.location.href,
      });
    }
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handlePrevImage = () => {
    if (place?.files && place.files.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? place.files.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (place?.files && place.files.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === place.files.length - 1 ? 0 : prev + 1
      );
    }
  };

  //   if (loading) {
  //     return (
  //       <Container maxWidth="sm" sx={{ py: 2 }}>
  //         <Typography>Loading place details...</Typography>
  //       </Container>
  //     );
  //   }

  if (!place) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography>Place not found</Typography>
      </Container>
    );
  }

  const handleClick = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${place?.location?.lat},${place?.location?.lng}`;
    window.open(url, "_blank"); // Open in new tab or Google Maps app on mobile
  };

  const mainImage = place.files && place.files.length > 0
    ? place.files[0].fileURL
    : 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';

  const averageRating = place.rating || 4;

  // Dynamically choose a color for the rating badge
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#2E7D32'; // green for high ratings
    if (rating >= 3) return '#F9A825'; // amber for medium ratings
    if (rating > 0) return '#EF5350'; // red for low ratings
    return '#BDBDBD'; // grey when there is no rating
  };

  const handleExplore = () => {
    navigate(`/places/${id}/posts`, { state: { placeId: place._id } })
  }

  const handleMarkAsVisited = async (placeId: string) => {
    if (!place) return;
    
    try {
      const res = await request<{ status: boolean, data: { isVisited: boolean } }>(`/profile/visited-places/${placeId}`, {
        authorization: true,
        method: 'POST',
      });
      
      if (res.status === true) {
        const result = res.data.isVisited;
        // Create the updated place object
        const updatedPlace = { ...place, isVisited: result };
        // Update local state first
        setPlace(updatedPlace);
        // Then update the store
        // dispatch(updatePlaceReview(updatedPlace));
        
      }
    } catch (err) {
      console.error('Error marking place as visited:', err);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ flex: 1, }}>
      <TopNavBar title={place.name} canGoBack onBackClick={() => navigate('/')} />
      

      <PlaceTabs value={tabValue} onChange={(e) => {
        setTabValue(e)
      }} />

 
      {tabValue === 0 &&
        <>

          {/* Image Carousel */}
          <Box sx={{ p: 3, mt: 0, borderRadius: '16px', borderColor: 'grey', borderWidth: '1px ', borderStyle: '-moz-initial', my: 2, bgcolor: 'white', position: 'relative' }}>
            {/* Main Image Display */}

            <Box sx={{ position: 'relative', width: '100%', height: '300px', overflow: 'hidden', borderRadius: '12px' }}>
              <Box
                sx={{
                  display: 'flex',
                  width: `${(place?.files?.length || 1) * 100}%`,
                  height: '100%',
                  transform: `translateX(-${currentImageIndex * (100 / (place?.files?.length || 1))}%)`,
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                {place?.files && place.files.length > 0 ? (
                  place.files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: `${100 / place.files.length}%`,
                        height: '100%',
                        flexShrink: 0,
                      }}
                    >
                      <ImageWithLoader
                        src={file.fileURL}
                        alt={`${place.name} - Image ${index + 1}`}
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        containerSx={{ width: '100%', height: '100%' }}
                      />
                    </Box>
                  ))
                ) : (
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <ImageWithLoader
                      src={mainImage}
                      alt={place.name}
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      containerSx={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                )}
              </Box>


              {/* Navigation Arrows */}
              {place?.files && place.files.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Indicators */}
            {place?.files && place.files.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                {place.files.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: currentImageIndex === index ? '#ff6b35' : 'rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: currentImageIndex === index ? '#ff6b35' : 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  />
                ))}
              </Box>
            )}



            <Box sx={{
              position: 'absolute',
              top: 26,
              right: 26,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <IconButton
                onClick={() => {
                  const placeId = place._id;
                  handleFavoriteToggle(placeId)
                }}
                sx={{
                  bgcolor: 'white',
                  '&:hover': { bgcolor: 'white' },
                  boxShadow: 1
                }}
              >
                {place.isLiked ? <Favorite sx={{ color: 'red' }} /> : <FavoriteBorder />}
              </IconButton>
              <Chip
                label={`${currentImageIndex + 1} / ${place?.files?.length || 1}`}
                size="small"
                sx={{
                  bgcolor: 'white',
                  fontWeight: 'bold',
                  boxShadow: 1
                }}
              />
            </Box>
          </Box>

          {/* Place Details */}
          <Paper sx={{ p: 3, mt: 0, borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography fontWeight='medium' gutterBottom>
                  {place.name}
                </Typography>
                <CircleIcon sx={{ fontSize: 6, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {distance ? `${distance}km` : place.distance || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                icon={place.isVisited ? <Check sx={{ color: '#008387',fontSize:16 }} />: undefined}
                onClick={() => {
                  handleMarkAsVisited(place._id)
                }} label={place.isVisited ? "Visited" : "Mark as visited"} variant='outlined' sx={{ borderRadius: 1, borderWidth: 1, borderColor:place.isVisited ? '#008387' : '#CAC4D0',color:place.isVisited ? '#008387' : 'black' ,py:2.5}} />
                
                {/* Rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: getRatingColor(averageRating), color: 'white', paddingX: 2, paddingY: 1, borderRadius: '8px' }}>
                  <Typography color='white'>{averageRating}</Typography>
                  <StarIcon sx={{ color: 'white' }} />
                </Box>
              </Box>
            </Box>


            {/* Category Tags */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {place.categories?.map((category, index) => (
                <Chip
                  key={index}
                  variant='outlined'
                  label={category}
                  size="small"
                  sx={{ borderColor: '#f0f0f0', color: '#666', borderRadius: '8px', paddingX: 1, paddingY: 0.5, fontVariant: 'medium' }}
                />
              ))}
            </Stack>



            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {place.description || "Perched on dramatic cliffs overlooking the Aegean Sea, Santorini is a postcard-perfect island known for its whitewashed buildings with blue domes."}
            </Typography>
          </Paper>

          {/* Map Section */}
          <Paper sx={{ p: 2, mt: 1, borderRadius: '16px', bgcolor: 'white' }}>
            {place.location && (
              <Box onClick={() => {
                handleClick()
              }}>

                <StaticMap
                  location={{
                    coordinates: {
                      lat: place?.location?.lat,
                      lng: place?.location?.lng
                    },
                    // zoom: place?.location?.zoom || 25
                  }}
                  height={200}
                  width="100%"
                  borderRadius="16px"
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              <MapOutlined
                sx={{ fontSize: 20, color: 'text.secondary', mx: 2 }}
              />
              <Box onClick={() => {
                handleClick()
              }}>
                <Typography variant="body2" fontWeight="500">
                  {place.address}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            {place.owner_details?.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {place.owner_details.phone}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <IconButton onClick={() => {
                window.open(place.social.find(item => item.key === 'instagram')?.value, '_blank');
              }} sx={{ bgcolor: '#E1306C', color: 'white', '&:hover': { bgcolor: '#C13584' } }}>
                <LanguageTwoTone />
              </IconButton>
              <IconButton onClick={() => {
                window.open(place.social.find(item => item.key === 'facebook')?.value, '_blank');
              }} sx={{ bgcolor: '#1877F2', color: 'white', '&:hover': { bgcolor: '#166FE5' } }}>
                <Facebook />
              </IconButton>
              <IconButton sx={{ bgcolor: '#E1306C', color: 'white', '&:hover': { bgcolor: '#C13584' } }}>
                <Instagram />
              </IconButton>
              <IconButton sx={{ bgcolor: '#FF0000', color: 'white', '&:hover': { bgcolor: '#CC0000' } }}>
                <YouTube />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            {/* Report Listing */}
            <Box 
              onClick={() => !isAlreadyReported && setReportModalOpen(true)} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: isAlreadyReported ? 'not-allowed' : 'pointer',
                opacity: isAlreadyReported ? 0.6 : 1,
                '&:hover': { bgcolor: isAlreadyReported ? 'transparent' : '#f5f5f5' }, 
                p: 1, 
                borderRadius: 1 
              }}
            >
              <Flag sx={{ mr: 1, color: isAlreadyReported ? '#9E9E9E' : '#BA1A1A', fontSize: 20 }} />
              <Typography variant="body2" color={isAlreadyReported ? '#9E9E9E' : '#BA1A1A'} fontWeight="500">
                {isAlreadyReported ? 'Already reported' : 'Report listing'}
              </Typography>
            </Box>
          </Paper>




          {/* Reviews Section */}
          <Typography onClick={() => {
            navigate(`/places/${id}/reviews`)
          }} variant="h6" fontWeight="600" sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            Reviews <ArrowForwardIos sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Typography>
        

          <Button
            onClick={() => setOpenReview(true)}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: '100px',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#F57C00',
              color: '#F57C00',
              '&:hover': { bgcolor: '#F57C00' },
            }}
          >
            Post review
          </Button>

          {/* Review List */}
       
          {place?.topReviews?.map((rev) => (
            <ReviewCard
              key={`${rev.user._id}-${rev.ratedAt}`}
              userName={rev.user.username}
              avatar={rev.user?.profilePhoto?.url || ''}
              date={rev.ratedAt}
              rating={rev.rating}
              review={rev.review}
            />
          ))}

          {/* Add Review Dialog */}
          <AddReviewDialog
            open={openReview}
            onClose={() => setOpenReview(false)}
            onSubmit={(data) => {
              createReview(place._id, data);
              setOpenReview(false);
            }}
          />
          
          {/* Report Modal */}
          <ReportModal
            open={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            onSubmit={handleReportPlace}
            title="Report this listing"
          />
          {/* Bottom spacing */}
          <Box sx={{ height: 100 }} />
        </>}
      {tabValue === 1 && <>
        <Typography>Posts</Typography>
        <PlaceRelatedPost placeId={place?._id} type="posts" />
      </>}
      {tabValue === 2 && <>
        <Typography>Shorts</Typography>
        <PlaceRelatedPost placeId={place?._id} type="shorts" />
      </>}
    </Container>
  );
};

export default PlaceDetail; 